'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { transformApiData, extractPagination } from './utils/formatters';

// Filter components
import FilterButtons from './filters/FilterButtons';
import AppliedFilters from './filters/AppliedFilters';
import MonthSelector from './filters/MonthSelector';

// Filter modal components
import LevelFilterModal from './filters/modals/LevelFilterModal';
import SizeFilterModal from './filters/modals/SizeFilterModal';
import SessionFilterModal from './filters/modals/SessionFilterModal';
import ClassTimeFilterModal from './filters/modals/ClassTimeFilterModal';
import FormatFilterModal from './filters/modals/FormatFilterModal';
import StatusFilterModal from './filters/modals/StatusFilterModal';

// Course components
import CourseTable from './course/CourseTable';
import CourseList from './course/CourseList';

// Common components
import LoadMoreButton from './common/LoadMoreButton';
import Pagination from './common/Pagination';

// Collection search endpoint (proxied via port 3000 → 8001)
const COLLECTION_SEARCH_URL = '/api/globals/collection/Courses/search';

// Initial filter buttons
const initialFilterButtons = [
  {
    id: 'level',
    label: 'Trình độ',
    icon: null,
  },
  {
    id: 'size',
    label: 'Sĩ số',
    icon: null,
  },
  {
    id: 'session',
    label: 'Buổi',
    icon: null,
  },
  {
    id: 'classTime',
    label: 'Ca học',
    icon: null,
  },
  {
    id: 'format',
    label: 'Hình thức',
    icon: null,
  },
  {
    id: 'status',
    label: 'Trạng thái',
    icon: null,
  },
];

interface Course {
  _id: string;
  name: string;
  description: string;
  courseImage?: string;
  startDate: string;
  classDays: string[];
  timeSlots: any[];
  maxStudents: number;
  enrolledCount: number;
  level: any;
  format: string;
  status: string;
  price: number;
  category?: string;
}

interface PaginationInfo {
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
}

interface CourseScheduleTableProps {
  courceCategory?: string;
}

const CourseScheduleTable: React.FC<CourseScheduleTableProps> = ({
  courceCategory = 'ielts',
}) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [visibleCourses, setVisibleCourses] = useState(8);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [newlyLoadedCourses, setNewlyLoadedCourses] = useState<string[]>([]);
  const [isMobileView, setIsMobileView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterButtons] = useState(initialFilterButtons);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
    totalDocs: 0,
    limit: 10,
    totalPages: 0,
    page: 1,
    hasPrevPage: false,
    hasNextPage: false,
  });
  const [isFetching, setIsFetching] = useState(true);

  // Check if screen is small for responsive design
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close filter when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click is not on filter button or modal
      if (
        !target.closest('[data-filter-button]') &&
        !target.closest('[data-filter-modal]')
      ) {
        setAnchorEl(null);
        setActiveFilter(null);
      }
    };

    if (activeFilter) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeFilter]);

  // Fetch courses from CollectionController
  const fetchCourses = useCallback(
    async (page = 1, filters: Record<string, any> = {}) => {
      setIsFetching(true);
      setLoading(true);

      try {
        // Build filterGroup AND conditions
        const groups: any[] = [];

        // Category filter (always applied)
        if (courceCategory) {
          groups.push({ field: 'category', op: { $eq: courceCategory } });
        }

        // Month range filter
        if (currentMonth) {
          const year = new Date().getFullYear();
          const monthStart = new Date(year, currentMonth - 1, 1).toISOString();
          const monthEnd   = new Date(year, currentMonth, 0, 23, 59, 59).toISOString();
          groups.push({ field: 'startDate', op: { $gte: monthStart, $lte: monthEnd } });
        }

        // Dynamic filters from UI
        for (const [key, value] of Object.entries(filters)) {
          if (!value) continue;

          switch (key) {
            case 'level':
              // lookup filter: levelId → EnglishLevels.name
              groups.push({
                field: 'levelId',
                extractProp: 'name',
                op: { $eq: value },
                matchMode: 'any',
              });
              break;

            case 'size':
              if (value === '< 10 học viên')
                groups.push({ field: 'maxStudents', op: { $lt: 10 } });
              else if (value === '10-15 học viên')
                groups.push({ field: 'maxStudents', op: { $gte: 10, $lte: 15 } });
              else if (value === '> 15 học viên')
                groups.push({ field: 'maxStudents', op: { $gt: 15 } });
              break;

            case 'session':
              // classDays is a plain string array — MongoDB matches any element
              groups.push({ field: 'classDays', op: { $eq: value === 'Chủ nhật' ? 'CN' : value.replace('Thứ ', 'T') } });
              break;

            case 'classTime':
              if (value === 'Sáng <12h')
                // timeSlots[0].startTime — dot notation into nested array element
                groups.push({ field: 'timeSlots.startTime', op: { $lt: '12:00' } });
              else if (value === 'Chiều <22h')
                groups.push({ field: 'timeSlots.startTime', op: { $gte: '12:00', $lt: '22:00' } });
              break;

            case 'format':
              groups.push({ field: 'format', op: { $eq: value === 'Học Tại Lớp' ? 'offline' : 'online' } });
              break;

            case 'status': {
              const statusMap: Record<string, string> = {
                'Đang mở đăng ký': 'open',
                'Đã đầy': 'full',
                'Đã kết thúc': 'closed',
              };
              const sv = statusMap[value];
              if (sv) groups.push({ field: 'status', op: { $eq: sv } });
              break;
            }
          }
        }

        const body: Record<string, any> = {
          page,
          limit: 10,
          sortBy: 'startDate',
          sortOrder: 'asc',
        };

        if (groups.length > 0) {
          body.filterGroup = { type: 'and', groups };
        }

        const response = await fetch(COLLECTION_SEARCH_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const json = await response.json();
        const transformedCourses = transformApiData(json);
        const pagination = extractPagination(json);

        setCourses(transformedCourses);
        setVisibleCourses(Math.min(8, transformedCourses.length));
        setPaginationInfo(pagination);

        if (page === 1 && Object.keys(filters).length === 0 && allCourses.length === 0) {
          setAllCourses(transformedCourses);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Không thể tải khóa học. Vui lòng thử lại sau.');
      } finally {
        setTimeout(() => {
          setLoading(false);
          setIsFetching(false);
        }, 300);
      }
    },
    [courceCategory, allCourses.length, currentMonth]
  );

  // Initial data fetch
  useEffect(() => {
    fetchCourses(1);
  }, [courceCategory, fetchCourses]);

  // Fetch when month changes
  useEffect(() => {
    fetchCourses(1, appliedFilters);
  }, [currentMonth, fetchCourses, appliedFilters]);

  const openFilter = (filterId: string, element: HTMLElement) => {
    setAnchorEl(element);
    setActiveFilter(filterId);
  };

  const closeFilter = () => {
    setAnchorEl(null);
    setActiveFilter(null);
  };

  const applyFilter = (filterId: string, option: string) => {
    const newFilters = {
      ...appliedFilters,
      [filterId]: option,
    };

    setAppliedFilters(newFilters);
    closeFilter();

    // Reset to page 1 when applying filters
    fetchCourses(1, newFilters);
  };

  const removeFilter = (key: string) => {
    const newFilters = { ...appliedFilters };
    delete newFilters[key];
    setAppliedFilters(newFilters);
    fetchCourses(1, newFilters);
  };

  const clearAllFilters = () => {
    setAppliedFilters({});
    fetchCourses(1, {});
  };

  const loadMore = () => {
    if (paginationInfo.hasNextPage) {
      fetchCourses(paginationInfo.page + 1, appliedFilters);
    } else {
      const prevCount = visibleCourses;
      const newCount = Math.min(prevCount + 5, courses.length);
      setVisibleCourses(newCount);

      // Mark newly loaded courses for animation
      const newIds = courses.slice(prevCount, newCount).map((c) => c._id);
      setNewlyLoadedCourses(newIds);

      // Clear the newly loaded courses after animation completes
      setTimeout(() => {
        setNewlyLoadedCourses([]);
      }, 1000);
    }
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentMonth(parseInt(e.target.value));
  };

  const prevMonth = () => {
    setCurrentMonth((prev) => (prev > 1 ? prev - 1 : 12));
  };

  const nextMonth = () => {
    setCurrentMonth((prev) => (prev < 12 ? prev + 1 : 1));
  };

  const handleSortByDate = () => {
    const sorted = [...courses].sort((a, b) => {
      const dateA = new Date(a.startDate.split('/').reverse().join('-'));
      const dateB = new Date(b.startDate.split('/').reverse().join('-'));
      return dateA.getTime() - dateB.getTime();
    });
    setCourses(sorted);
  };

  return (
    <div className="dark:bg-gray-900 text-gray-800 dark:text-gray-200 lg:min-h-[600px]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            Lịch khai giảng khóa học
          </h1>
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-4">
            Lịch học với thời gian học linh hoạt trong tuần giúp bạn thoải mái trong việc
            sắp xếp thời gian học tập tốt nhất
          </p>
        </div>

        {/* Filter Buttons */}
        <FilterButtons
          filterOptions={filterButtons}
          appliedFilters={appliedFilters}
          openFilter={openFilter}
          isDisabled={isFetching}
        />

        {/* Month Selector */}
        <MonthSelector
          currentMonth={currentMonth}
          handleMonthChange={handleMonthChange}
          prevMonth={prevMonth}
          nextMonth={nextMonth}
          totalDocs={paginationInfo.totalDocs}
          handleSortByDate={handleSortByDate}
          isDisabled={isFetching}
        />

        {/* Applied Filters */}
        <AppliedFilters
          appliedFilters={appliedFilters}
          filterOptions={filterButtons}
          removeFilter={removeFilter}
          clearAllFilters={clearAllFilters}
          isDisabled={isFetching}
        />

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4">
            <strong className="font-bold">Lỗi!</strong>
            <span className="block sm:inline ml-1">{error}</span>
            <button
              className="absolute top-0 bottom-0 right-0 px-4 py-3"
              onClick={() => fetchCourses(1, appliedFilters)}
            >
              <svg
                className="fill-current h-6 w-6 text-red-500"
                role="button"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
              </svg>
            </button>
          </div>
        )}

        {/* No Courses Found */}
        {!isFetching && !error && courses.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              Không tìm thấy khóa học
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Không có khóa học nào phù hợp với bộ lọc đã chọn.
            </p>
            <div className="mt-6">
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#017e6e] hover:bg-[#015d5b] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#017e6e]"
              >
                Xóa bộ lọc
              </button>
            </div>
          </div>
        )}

        {/* Course Content - Table or List */}
        {!error && courses.length > 0 &&
          (isMobileView ? (
            <CourseList
              courses={courses}
              visibleCourses={visibleCourses}
              newlyLoadedCourses={newlyLoadedCourses}
              isLoading={isFetching}
            />
          ) : (
            <CourseTable
              courses={courses}
              visibleCourses={visibleCourses}
              newlyLoadedCourses={newlyLoadedCourses}
              isLoading={isFetching}
            />
          ))}

        {/* Load More Button */}
        {!isFetching && !error && courses.length > 0 && (
          <LoadMoreButton
            visibleCourses={visibleCourses}
            coursesLength={courses.length}
            loadMore={loadMore}
          />
        )}

        {/* Pagination */}
        {!isFetching &&
          !error &&
          paginationInfo.totalPages > 1 && (
            <Pagination
              paginationInfo={paginationInfo}
              fetchCourses={fetchCourses}
              appliedFilters={appliedFilters}
            />
          )}

        {/* Render the appropriate filter modal based on activeFilter */}
        <AnimatePresence>
          {activeFilter === 'level' && (
            <LevelFilterModal
              isOpen={true}
              closeFilter={closeFilter}
              applyFilter={applyFilter}
              appliedFilter={appliedFilters.level}
              anchorEl={anchorEl}
            />
          )}

          {activeFilter === 'size' && (
            <SizeFilterModal
              isOpen={true}
              closeFilter={closeFilter}
              applyFilter={applyFilter}
              appliedFilter={appliedFilters.size}
              anchorEl={anchorEl}
            />
          )}

          {activeFilter === 'session' && (
            <SessionFilterModal
              isOpen={true}
              closeFilter={closeFilter}
              applyFilter={applyFilter}
              appliedFilter={appliedFilters.session}
              courses={allCourses}
              anchorEl={anchorEl}
            />
          )}

          {activeFilter === 'classTime' && (
            <ClassTimeFilterModal
              isOpen={true}
              closeFilter={closeFilter}
              applyFilter={applyFilter}
              appliedFilter={appliedFilters.classTime}
              anchorEl={anchorEl}
            />
          )}

          {activeFilter === 'format' && (
            <FormatFilterModal
              isOpen={true}
              closeFilter={closeFilter}
              applyFilter={applyFilter}
              appliedFilter={appliedFilters.format}
              anchorEl={anchorEl}
            />
          )}

          {activeFilter === 'status' && (
            <StatusFilterModal
              isOpen={true}
              closeFilter={closeFilter}
              applyFilter={applyFilter}
              appliedFilter={appliedFilters.status}
              anchorEl={anchorEl}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CourseScheduleTable;
